import React, { useState, useEffect } from "react";
import "./OrderPrint.css";

const OrderPrint = ({
  selectedOrderBook,
  orderItemList,
  clientInfo,
  printClientInfo,
}) => {
  const [resolvedClientInfo, setResolvedClientInfo] = useState(null);

  useEffect(() => {
    if (!selectedOrderBook) return;

    const isEmptyInfo = (val) =>
      !val ||
      (Array.isArray(val) && val.length === 0) ||
      (typeof val === "object" &&
        !Array.isArray(val) &&
        Object.keys(val).length === 0);

    // 인쇄용 거래처정보 우선 사용 (관리자 모드, 인쇄 버튼으로 로드됨)
    if (!isEmptyInfo(printClientInfo)) {
      setResolvedClientInfo(printClientInfo);
    } else if (!isEmptyInfo(clientInfo)) {
      // clientInfo가 배열이면 첫 번째 항목, 아니면 그대로 사용
      setResolvedClientInfo(
        Array.isArray(clientInfo) ? clientInfo[0] : clientInfo,
      );
    }
  }, [selectedOrderBook, clientInfo, printClientInfo]);

  if (!selectedOrderBook || orderItemList.length === 0) {
    return null;
  }

  const formatBusinessNo = (no) => {
    if (!no) return "";
    const value = no.replace(/\D/g, "");
    if (value.length === 10) {
      return `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5, 10)}`;
    }
    return no;
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber || typeof phoneNumber !== "string") return "";

    const value = phoneNumber.replace(/\D/g, "");
    const len = value.length;

    // 02로 시작하는 서울 지역번호 (2자리)
    if (value.startsWith("02")) {
      if (len === 9) {
        return `${value.slice(0, 2)}-${value.slice(2, 5)}-${value.slice(5)}`;
      } else if (len === 10) {
        return `${value.slice(0, 2)}-${value.slice(2, 6)}-${value.slice(6)}`;
      }
    }

    // 기타 지역번호 또는 휴대폰 (3자리로 시작)
    if (len === 10) {
      return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
    } else if (len === 11) {
      return `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7)}`;
    }

    // 8자리 공통번호 (예: 1588-1234)
    if (len === 8) {
      return `${value.slice(0, 4)}-${value.slice(4)}`;
    }

    // 그 외 (길이가 7자리 이하 등)
    if (len > 3) {
      const prefix = value.startsWith("02") ? 2 : 3;
      if (len > prefix) {
        return `${value.slice(0, prefix)}-${value.slice(prefix)}`;
      }
    }

    return value;
  };

  // resolvedClientInfo 우선, 없으면 clientInfo 사용 (단, 배열/빈값이면 {} 처리)
  const safeInfo = (val) =>
    val &&
    !Array.isArray(val) &&
    typeof val === "object" &&
    Object.keys(val).length > 0
      ? val
      : null;
  const info = safeInfo(resolvedClientInfo) || safeInfo(clientInfo) || {};

  const {
    OBserial,
    OrderDate,
    Client,
    DeputyName,
    Tel,
    clientRequest,
    TotalAmount,
  } = selectedOrderBook;

  const formatDate = (dateString) => {
    if (!dateString) return "20    년    월    일";
    const year = dateString.slice(0, 2);
    const month = dateString.slice(2, 4);
    const day = dateString.slice(4, 6);
    return `20${year} 년 ${month} 월 ${day} 일`;
  };

  const formatNumber = (value) => {
    if (!value) return "0";
    const number = parseInt(value.toString().replace(/,/g, ""), 10);
    return isNaN(number) ? "0" : number.toLocaleString();
  };

  const totalAmount = Array.isArray(orderItemList)
    ? orderItemList.reduce((sum, item) => {
        const amount = parseInt(
          item.Amount?.toString().replace(/,/g, "") || "0",
          10,
        );
        return sum + (isNaN(amount) ? 0 : amount);
      }, 0)
    : 0;

  const currentPrintDate = new Date().toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  // 숫자를 한글 금액으로 변환 (일금 OOO 원정)
  const numberToKorean = (num) => {
    if (!num || num === 0) return "영";

    const bigUnits = ["", "만", "억", "조"];
    const unitNames = ["", "십", "백", "천"];
    const korNums = ["", "일", "이", "삼", "사", "오", "육", "칠", "팔", "구"];

    let result = "";
    let n = num;
    let blockIdx = 0;

    while (n > 0) {
      const block = n % 10000; // 4자리 블록 추출
      n = Math.floor(n / 10000);

      if (block > 0) {
        const thousands = Math.floor(block / 1000);
        const hundreds = Math.floor((block % 1000) / 100);
        const tens = Math.floor((block % 100) / 10);
        const ones = block % 10;

        let blockStr = "";
        if (thousands > 0) blockStr += korNums[thousands] + "천";
        if (hundreds > 0) blockStr += korNums[hundreds] + "백";
        if (tens > 0) blockStr += korNums[tens] + "십";
        if (ones > 0) blockStr += korNums[ones];

        result = blockStr + bigUnits[blockIdx] + (result ? " " + result : "");
      }
      blockIdx++;
    }

    return result || "영";
  };

  const ITEMS_PER_PAGE = 15; // 빈칸 포함 총 15줄 (행당 2줄 높이 기준)
  const totalPages = Math.ceil(
    Math.max((orderItemList || []).length, 1) / ITEMS_PER_PAGE,
  );

  const pages = Array.from({ length: totalPages }, (_, i) => {
    const startIdx = i * ITEMS_PER_PAGE;
    const pageItems = (orderItemList || []).slice(
      startIdx,
      startIdx + ITEMS_PER_PAGE,
    );
    while (pageItems.length < ITEMS_PER_PAGE) {
      pageItems.push({ isBlank: true, id: Math.random() });
    }
    return pageItems;
  });

  return (
    <div className="print-only">
      {pages.map((paddedItems, pageIndex) => (
        <div
          key={pageIndex}
          className="order-print-container"
          style={{
            pageBreakAfter: pageIndex < totalPages - 1 ? "always" : "auto",
          }}
        >
          <h2 className="print-title" style={{ marginBottom: "10px" }}>
            발 주 서
          </h2>
          <div
            style={{
              textAlign: "left",
              fontSize: "17px",
              fontWeight: "bold",
              marginBottom: "4px",
            }}
          >
            No. <span style={{ textDecoration: "underline" }}>{OBserial}</span>
          </div>

          <div style={{ display: "flex", border: "3px solid #000" }}>
            {/* Left Side: 발주처 테이블 */}
            <div style={{ width: "65%", borderRight: "3px solid #000" }}>
              <table
                className="formal-table"
                style={{ border: "none", margin: 0 }}
              >
                <colgroup>
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "22%" }} />
                  <col style={{ width: "26%" }} />
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "25%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td
                      className="header-cell"
                      rowSpan="5"
                      style={{
                        fontSize: "19px",
                        letterSpacing: "2px",
                        borderTop: "none",
                        borderLeft: "none",
                      }}
                    >
                      발<br />
                      <br />주<br />
                      <br />처
                    </td>
                    <td className="header-cell" style={{ borderTop: "none" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "0 10px",
                        }}
                      >
                        <span>사</span>
                        <span>업</span>
                        <span>자</span>
                        <span>번</span>
                        <span>호</span>
                      </div>
                    </td>
                    <td
                      colSpan="3"
                      className="data-cell text-center"
                      style={{
                        fontSize: "21px",
                        fontWeight: "bold",
                        letterSpacing: "1px",
                        borderTop: "none",
                        borderRight: "none",
                      }}
                    >
                      {formatBusinessNo(info?.BusinessNo)}
                    </td>
                  </tr>
                  <tr>
                    <td className="header-cell">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "0 10px",
                        }}
                      >
                        <span>회</span>
                        <span>사</span>
                        <span>명</span>
                      </div>
                    </td>
                    <td className="data-cell">
                      {Client || info?.ClientName || ""}
                    </td>
                    <td className="header-cell">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "0 10px",
                        }}
                      >
                        <span>대</span>
                        <span>표</span>
                        <span>자</span>
                      </div>
                    </td>
                    <td
                      className="data-cell text-center"
                      style={{
                        borderRight: "none",
                        paddingLeft: "5pt",
                        textAlign: "left",
                      }}
                    >
                      {DeputyName || info?.DeputyName || ""} (인)
                    </td>
                  </tr>
                  <tr>
                    <td className="header-cell">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "0 10px",
                        }}
                      >
                        <span>주</span>
                        <span>소</span>
                      </div>
                    </td>
                    <td
                      colSpan="3"
                      className="data-cell"
                      style={{ borderRight: "none" }}
                    >
                      {info?.BusinessAddr || ""}
                    </td>
                  </tr>
                  <tr>
                    <td className="header-cell">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "0 10px",
                        }}
                      >
                        <span>업</span>
                        <span>태</span>
                      </div>
                    </td>
                    <td className="data-cell">{info?.BusinessArea || ""}</td>
                    <td className="header-cell">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "0 10px",
                        }}
                      >
                        <span>종</span>
                        <span>목</span>
                      </div>
                    </td>
                    <td
                      className="data-cell"
                      style={{ borderRight: "none", paddingLeft: "5pt" }}
                    >
                      {info?.BusinessKind || ""}
                    </td>
                  </tr>
                  <tr>
                    <td
                      className="header-cell"
                      style={{ borderBottom: "none" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "0 10px",
                        }}
                      >
                        <span>연</span>
                        <span>락</span>
                        <span>처</span>
                      </div>
                    </td>
                    <td className="data-cell" style={{ borderBottom: "none" }}>
                      {formatPhoneNumber(Tel || info?.Tel)}
                    </td>
                    <td
                      className="header-cell"
                      style={{ borderBottom: "none" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "0 10px",
                        }}
                      >
                        <span>이</span>
                        <span>메</span>
                        <span>일</span>
                      </div>
                    </td>
                    <td
                      className="data-cell"
                      style={{
                        borderBottom: "none",
                        borderRight: "none",
                        paddingLeft: "5pt",
                      }}
                    >
                      {info?.Email || ""}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Right Side: 일자, 귀하, 아래와 같이 발주합니다. */}
            <div
              style={{
                width: "35%",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                padding: "10px",
              }}
            >
              <div style={{ fontSize: "17px" }}>
                주문일자 :{" "}
                <span style={{ textDecoration: "underline" }}>
                  {formatDate(OrderDate)}
                </span>
              </div>
              <div
                style={{
                  textAlign: "right",
                  marginTop: "40px",
                  fontSize: "27px",
                  fontWeight: "bold",
                  letterSpacing: "2px",
                  paddingRight: "10px",
                }}
              >
                <span style={{ textDecoration: "underline" }}>
                  친우메드 귀하
                </span>
              </div>
              <div
                style={{
                  position: "absolute",
                  bottom: "15px",
                  width: "100%",
                  textAlign: "center",
                  fontSize: "18px",
                  fontWeight: "bold",
                  paddingRight: "20px",
                }}
              >
                아래와 같이 발주합니다.
              </div>
            </div>
          </div>

          <table className="formal-table border-top-none">
            <colgroup>
              <col style={{ width: "15%" }} />
              <col style={{ width: "85%" }} />
            </colgroup>
            <tbody>
              <tr>
                <td
                  className="header-cell thick-bottom-border"
                  style={{ borderTop: "3px solid #000" }}
                >
                  합계금액
                </td>
                <td
                  className="data-cell thick-bottom-border"
                  style={{ borderTop: "3px solid #000", paddingLeft: "15px" }}
                >
                  일금 {numberToKorean(totalAmount)} 원정 (₩{" "}
                  {formatNumber(totalAmount)}), 세액 포함
                </td>
              </tr>
            </tbody>
          </table>

          <table className="formal-table border-top-none">
            <colgroup>
              <col style={{ width: "5%" }} />
              <col style={{ width: "35%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "18%" }} />
            </colgroup>
            <thead>
              <tr>
                <th className="header-cell">No</th>
                <th className="header-cell">품 목</th>
                <th className="header-cell">모 델 명</th>
                <th className="header-cell">수 량</th>
                <th className="header-cell">단 가</th>
                <th className="header-cell">금 액</th>
              </tr>
            </thead>
            <tbody>
              {paddedItems.map((item, index) => {
                if (item.isBlank) {
                  return (
                    <tr key={item.id} className="item-row">
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  );
                }
                return (
                  <tr key={item.OIserial} className="item-row">
                    <td className="text-center">
                      {pageIndex * ITEMS_PER_PAGE + index + 1}
                    </td>
                    <td className="text-left">
                      <div className="cell-clamp">{item.ItemName}</div>
                    </td>
                    <td className="text-left">
                      <div className="cell-clamp">{item.ModelName}</div>
                    </td>
                    <td className="text-center">{formatNumber(item.Qty)}</td>
                    <td className="text-right">
                      {formatNumber(item.UnitPrice || item.DiscountPrice)}
                    </td>
                    <td className="text-right">{formatNumber(item.Amount)}</td>
                  </tr>
                );
              })}
              <tr>
                <td
                  colSpan="6"
                  className="data-cell"
                  style={{
                    height: "150px",
                    verticalAlign: "top",
                    padding: "10px",
                  }}
                >
                  <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                    고객 요청사항:
                  </div>
                  <div>{clientRequest || ""}</div>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="print-current-date">
            <div className="date">
              출력일시 :{" "}
              <span style={{ textDecoration: "underline" }}>
                {currentPrintDate}
              </span>
            </div>
            <div className="page">
              {pageIndex + 1} / {totalPages}
            </div>
            <div className="empty" style={{ width: "33%" }}></div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default OrderPrint;
